/**
 * Stats — /(app)/settings/stats
 *
 * Shows the current user's lifetime stats by fetching their public profile
 * via GET /api/v1/users/:username. Reuses StatsPanel so the rendering is
 * identical to what /u/[username] shows for any other user.
 */

import { COMPACT_HEADER_STYLE } from '../../../lib/gameChrome'
import React from 'react'
import { View, Text, ScrollView, TouchableOpacity, ActivityIndicator, useWindowDimensions } from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useQuery } from '@tanstack/react-query'
import { useOrientation } from '../../../hooks/useOrientation'
import { useAuth } from '../../../hooks/useAuth'
import { apiGet } from '../../../lib/api'
import { goBackOr } from '../../../lib/goBack'
import { StatsPanel, type UserStats, type UnrankedStats } from '../../../components/profile/StatsPanel'
import { DiceLuckHistoryPanel } from '../../../components/profile/DiceLuckHistoryPanel'
import type { DiceLuckHistory } from '@warcarp/api-types'

interface PublicProfile {
  id: string
  username: string
  displayName: string
  stats: UserStats & { losses: number }
  /** Unranked games and ranked + unranked totals. Absent from older servers. */
  unrankedStats?: UnrankedStats
  combinedStats?: UserStats
  diceLuck?: DiceLuckHistory
  memberSince: string
}

function formatMemberSince(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
}

export default function MyStatsScreen() {
  const { user } = useAuth()
  const router = useRouter()
  const { width: winWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()
  const contentWidth = isLandscape ? Math.min(480, winWidth * 0.55) : undefined

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-public', user?.username],
    queryFn: () =>
      apiGet<PublicProfile>(
        `/api/v1/users/${encodeURIComponent(user?.username ?? '')}`,
      ),
    enabled: !!user?.username,
  })

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      {/* Header */}
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOr(router, '/(app)/settings/account')}
          accessibilityLabel="Back to general settings"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
        <Text className="text-ink font-stencil text-xl flex-1 uppercase">Your Profile</Text>
      </View>

      {isLoading && (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator color="#8aa14a" />
        </View>
      )}

      {error && !isLoading && (
        <View className="flex-1 items-center justify-center px-6">
          <Text className="font-typed text-ink-soft text-base text-center">
            Could not load stats. Pull to refresh.
          </Text>
        </View>
      )}

      {data && !isLoading && (
        <ScrollView
          className="flex-1"
          contentContainerStyle={{
            padding: 16,
            gap: 16,
            alignItems: isLandscape ? 'center' : undefined,
          }}
        >
          <View style={isLandscape ? { width: contentWidth, gap: 16 } : { width: '100%', gap: 16 }}>
          {/* Identity card */}
          <View className="bg-paper border-2 border-olive rounded-xl p-4 gap-3 items-center">
            <View className="items-center">
              <Text className="font-stencil text-ink text-2xl uppercase">{data.displayName}</Text>
              <Text className="font-typed text-ink-soft text-sm">@{data.username}</Text>
            </View>
            <Text className="font-typed text-ink-soft text-xs italic">
              // enlisted {formatMemberSince(data.memberSince)}
            </Text>
          </View>

          <StatsPanel stats={data.stats} unrankedStats={data.unrankedStats} combinedStats={data.combinedStats} />
          {data.diceLuck && (
            <DiceLuckHistoryPanel history={data.diceLuck} />
          )}
          </View>
        </ScrollView>
      )}
    </SafeAreaView>
  )
}
